<template>
  <!-- Second header row for Bookings and Traffic, matching the one the Tasks
       section has. Options come from the payload rather than being hardcoded,
       because every list here is the host's own data. -->
  <div class="flex flex-wrap items-center gap-2">
    <template v-if="tab === 'bookings'">
      <FilterDropdown
        label="Event" :options="options.events || []" :selected="filters.eventIds" swatches
        @update="v => store.setFilters('bookings', { eventIds: v })"
      />
      <FilterDropdown
        label="Status" :options="options.statuses || []" :selected="filters.statuses"
        @update="v => store.setFilters('bookings', { statuses: v })"
      />
      <FilterDropdown
        label="Location" :options="options.locations || []" :selected="filters.locations"
        @update="v => store.setFilters('bookings', { locations: v })"
      />
      <FilterSelect
        label="Booked by" :model-value="filters.bookedBy" :options="BOOKED_BY_OPTIONS" default-value=""
        @update:model-value="v => store.setFilters('bookings', { bookedBy: v })"
      />
    </template>

    <template v-else>
      <FilterDropdown
        label="Source" :options="options.channels || []" :selected="filters.channels"
        @update="v => store.setFilters('traffic', { channels: v })"
      />
      <FilterDropdown
        label="Country" :options="options.countries || []" :selected="filters.countries"
        @update="v => store.setFilters('traffic', { countries: v })"
      />
      <FilterDropdown
        label="Device" :options="options.devices || []" :selected="filters.devices"
        @update="v => store.setFilters('traffic', { devices: v })"
      />
      <FilterSelect
        label="Pages" :model-value="filters.pageType" :options="PAGE_TYPE_OPTIONS" default-value=""
        @update:model-value="v => store.setFilters('traffic', { pageType: v })"
      />
    </template>

    <button
      v-if="store.hasActiveFilters(tab)"
      type="button"
      @click="store.resetFilters(tab)"
      class="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
    >Reset</button>

    <div class="ml-auto">
      <ExportMenu :basename="`${tab}-analytics`" :payload="stats" :csv="csv" :stamp="stamp" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAnalyticsStore } from 'stores/analytics.js'
import FilterDropdown from './FilterDropdown.vue'
import FilterSelect from './FilterSelect.vue'
import ExportMenu from './ExportMenu.vue'
import { bookingsCsv, trafficCsv } from './analyticsCsv.js'

const props = defineProps({
  tab: { type: String, required: true },
})

const store = useAnalyticsStore()

const filters = computed(() => store.filters[props.tab])
const options = computed(() => store.optionsFor(props.tab))
const stats = computed(() => store.statsFor(props.tab))

// Descriptions rather than bare labels, because "booked by me" is not obvious:
// it means a meeting the host entered themselves, not one the public page
// produced, and the two answer very different questions about a dashboard.
const BOOKED_BY_OPTIONS = [
  { value: '', label: 'Anyone', description: 'Every booking, however it was created.' },
  { value: 'attendee', label: 'The attendee', description: 'Bookings that came through your public page.' },
  { value: 'host', label: 'Me (host)', description: 'Meetings you entered yourself on behalf of someone.' },
]

const PAGE_TYPE_OPTIONS = [
  { value: '', label: 'All pages', description: 'Your profile and every event page together.' },
  { value: 'profile', label: 'Profile only', description: 'People who landed on your profile page.' },
  { value: 'event', label: 'Event pages only', description: 'People who opened a specific event.' },
]

const stamp = computed(() =>
  stats.value ? `${stats.value.range.start}_to_${stats.value.range.end}` : ''
)

const csv = computed(() => {
  if (!stats.value) return null
  return props.tab === 'bookings'
    ? bookingsCsv(stats.value, filters.value, options.value)
    : trafficCsv(stats.value, filters.value)
})
</script>
